"use client";

import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useTranslations } from "next-intl";
import { useLocale } from "next-intl";
import { Link, usePathname, useRouter } from "@/i18n/navigation";
import { User, LayoutDashboard, LogOut, ChevronDown } from "lucide-react";

const locales = [
  { code: "fr", flag: "🇫🇷", label: "FR" },
  { code: "de", flag: "🇩🇪", label: "DE" },
  { code: "it", flag: "🇮🇹", label: "IT" },
  { code: "pt", flag: "🇵🇹", label: "PT" },
  { code: "en", flag: "🇬🇧", label: "EN" },
] as const;

function LanguageSwitcher({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();

  const current = locales.find((l) => l.code === locale) ?? locales[0];

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const switchLocale = (code: string) => {
    router.replace(pathname, { locale: code });
    setOpen(false);
  };

  return (
    <div ref={ref} className={`relative ${className ?? ""}`}>
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 text-sm font-medium text-graphite/70 hover:text-graphite transition-colors px-2 py-1 border border-transparent hover:border-border"
      >
        <span>{current.flag}</span>
        <span>{current.label}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-28 bg-offwhite border border-border shadow-sm z-50">
          {locales.map((l) => (
            <button
              key={l.code}
              onClick={() => switchLocale(l.code)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-warm-gray ${
                l.code === locale ? "text-swiss-red font-semibold" : "text-graphite/70"
              }`}
            >
              <span>{l.flag}</span>
              <span>{l.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SiteNav() {
  const [open, setOpen] = useState(false);
  const { user, loading, logout } = useAuth();
  const t = useTranslations("nav");

  const links = [
    { label: t("catalogue"), href: "/catalogue", isHash: false },
    { label: t("services"), href: "/#services", isHash: true },
    { label: t("processus"), href: "/#processus", isHash: true },
    { label: t("conseillerIA"), href: "/conseiller", isHash: false },
    { label: t("blog"), href: "/blog", isHash: false },
    { label: t("faq"), href: "/#faq", isHash: true },
  ];

  const handleLogout = async () => {
    await logout();
    window.location.href = "/";
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-offwhite border-b border-border">
      <div className="max-w-[1400px] mx-auto px-6 flex items-center justify-between h-16">
        <a href="/" className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-swiss-red flex items-center justify-center">
            <span className="text-white font-bold text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>H</span>
          </div>
          <span className="font-bold text-lg tracking-tight text-graphite" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Helvetic<span className="text-swiss-red">Cars</span>
          </span>
        </a>

        <div className="hidden md:flex items-center gap-8">
          {links.map((l) =>
            l.isHash ? (
              <a key={l.href} href={l.href} className="text-sm font-medium text-graphite/70 hover:text-swiss-red transition-colors">
                {l.label}
              </a>
            ) : (
              <Link key={l.href} href={l.href} className="text-sm font-medium text-graphite/70 hover:text-swiss-red transition-colors">
                {l.label}
              </Link>
            )
          )}

          <LanguageSwitcher />

          {!loading && (
            user ? (
              <div className="flex items-center gap-3">
                {user.role === "admin" ? (
                  <a
                    href="/dashboard"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-graphite bg-warm-gray px-4 py-2 hover:bg-graphite hover:text-white transition-colors"
                  >
                    <LayoutDashboard size={14} /> {t("dashboard")}
                  </a>
                ) : (
                  <Link
                    href={user.role === "garage" ? "/garage" : "/mon-espace"}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-graphite bg-warm-gray px-4 py-2 hover:bg-graphite hover:text-white transition-colors"
                  >
                    <LayoutDashboard size={14} /> {user.role === "garage" ? t("espaceGarage") : t("monEspace")}
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="inline-flex items-center gap-1.5 text-sm text-graphite/40 hover:text-swiss-red transition-colors px-2 py-2"
                  title={t("seDeconnecter")}
                >
                  <LogOut size={16} />
                </button>
              </div>
            ) : (
              <Link
                href="/login"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-graphite bg-warm-gray px-4 py-2 hover:bg-graphite hover:text-white transition-colors"
              >
                <User size={14} /> {t("espaceMembre")}
              </Link>
            )
          )}
        </div>

        <button className="md:hidden p-2" onClick={() => setOpen(!open)}>
          <div className="space-y-1.5">
            <span className={`block w-6 h-0.5 bg-graphite transition-transform ${open ? "rotate-45 translate-y-2" : ""}`} />
            <span className={`block w-6 h-0.5 bg-graphite transition-opacity ${open ? "opacity-0" : ""}`} />
            <span className={`block w-6 h-0.5 bg-graphite transition-transform ${open ? "-rotate-45 -translate-y-2" : ""}`} />
          </div>
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-offwhite border-t border-border pb-4">
          {links.map((l) =>
            l.isHash ? (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="block px-6 py-3 text-sm font-medium text-graphite/70 hover:text-swiss-red"
              >
                {l.label}
              </a>
            ) : (
              <Link
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="block px-6 py-3 text-sm font-medium text-graphite/70 hover:text-swiss-red"
              >
                {l.label}
              </Link>
            )
          )}

          {!loading && (
            user ? (
              <>
                {user.role === "admin" ? (
                  <a href="/dashboard" className="block px-6 py-3 text-sm font-semibold text-swiss-red">
                    {t("dashboard")}
                  </a>
                ) : (
                  <Link
                    href={user.role === "garage" ? "/garage" : "/mon-espace"}
                    className="block px-6 py-3 text-sm font-semibold text-swiss-red"
                  >
                    {user.role === "garage" ? t("espaceGarage") : t("monEspace")}
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="block px-6 py-3 text-sm text-graphite/40 hover:text-swiss-red w-full text-left"
                >
                  {t("seDeconnecter")}
                </button>
              </>
            ) : (
              <Link href="/login" className="block px-6 py-3 text-sm font-semibold text-swiss-red">
                {t("espaceMembre")}
              </Link>
            )
          )}

          <div className="px-6 py-3 border-t border-border mt-1">
            <LanguageSwitcher />
          </div>
        </div>
      )}
    </nav>
  );
}
